import { useMemo } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { motion } from 'framer-motion';

// ─── Types ──────────────────────────────────────────────────────────
export interface TipTrendDatum {
  date: string;
  totalYen: number;
  count: number;
}

export interface TipTrendChartProps {
  data: TipTrendDatum[];
}

// ─── Custom Tooltip ─────────────────────────────────────────────────
interface TooltipPayloadItem {
  value: number;
  dataKey: string;
  payload: TipTrendDatum;
}

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];

}) => {
  if (!active || !payload?.length) return null;

  const datum = payload[0].payload;

  return (
    <div className="rounded-lg border border-white/10 bg-black/80 px-4 py-3 shadow-lg backdrop-blur-sm">
      <p className="mb-1 text-xs font-medium text-gray-400">{datum.date}</p>
      <p className="text-sm font-bold text-neon-blue">
        ¥{datum.totalYen.toLocaleString()}
      </p>
      <p className="text-xs text-gray-300">
        {datum.count}件のサポート
      </p>
    </div>
  );
};

// ─── Component ──────────────────────────────────────────────────────
export const TipTrendChart = ({ data }: TipTrendChartProps) => {
  const gradientId = 'tipTrendGradient';

  const maxYen = useMemo(
    () => Math.max(...data.map((d) => d.totalYen), 0),
    [data],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="w-full"
    >
      <div className="rounded-xl border border-white/10 bg-black/30 p-5">
        <h3 className="mb-4 text-sm font-bold tracking-widest text-gray-400">
          サポート推移
        </h3>

        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00f3ff" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#00f3ff" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                dy={8}
              />

              <YAxis
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) =>
                  v >= 10000 ? `¥${(v / 10000).toFixed(0)}万` : `¥${v}`
                }
                width={56}
                domain={[0, maxYen > 0 ? 'auto' : 1000]}
              />

              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  stroke: 'rgba(0,243,255,0.2)',
                  strokeWidth: 1,
                }}
              />

              <Area
                type="monotone"
                dataKey="totalYen"
                stroke="#00f3ff"
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                dot={false}
                activeDot={{
                  r: 5,
                  fill: '#00f3ff',
                  stroke: '#000',
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </motion.div>
  );
};
